<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>

    <template #default>
      <div class="w-full max-w-5xl mx-auto">
        <Breadcrumbs :breadcrumbs="[
          { name: 'Dashboard', href: route('dashboard') },
          { name: 'Menu Halaman', href: route('menupages.index') },
        ]" />

        <div class="mt-4 mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Menu Halaman Dinamis</h3>
          <p class="mt-1 text-sm text-gray-600">
            Menu yang ditambahkan otomatis muncul di navigasi frontend, lengkap dengan hero & kontennya sendiri.
          </p>
        </div>

        <!-- Form -->
        <div class="mb-8 overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
          <div class="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-500 to-purple-600">
            <h4 class="text-sm font-bold text-white">{{ form.id ? "Edit Menu" : "Tambah Menu" }}</h4>
          </div>
          <div class="p-5 space-y-4">
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <InputLabel value="Label Menu *" />
                <input v-model="form.label" type="text" placeholder="mis. Akreditasi"
                  class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                <p v-if="errors.label" class="mt-1 text-xs text-red-600">{{ errors.label }}</p>
              </div>
              <div>
                <InputLabel value="Slug (opsional)" />
                <input v-model="form.slug" type="text" placeholder="otomatis dari label"
                  class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <InputLabel value="Ikon (class Font Awesome)" />
                <input v-model="form.icon" type="text" placeholder="fa-solid fa-book"
                  class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div class="flex items-end gap-4">
                <div class="w-28">
                  <InputLabel value="Urutan" />
                  <input v-model.number="form.order" type="number"
                    class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                </div>
                <label class="inline-flex items-center gap-2 pb-2 text-sm text-gray-700">
                  <input v-model="form.is_active" type="checkbox" class="rounded text-indigo-600 focus:ring-indigo-500" />
                  Aktif
                </label>
              </div>
            </div>

            <!-- Hero -->
            <div class="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h5 class="mb-3 text-xs font-semibold tracking-wide text-gray-700 uppercase">🎯 Hero Halaman</h5>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <InputLabel value="Judul Hero" />
                  <input v-model="form.hero_title" type="text" placeholder="default: label"
                    class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <InputLabel value="Subjudul Hero" />
                  <input v-model="form.hero_subtitle" type="text"
                    class="block w-full px-3 py-2.5 mt-1 text-sm border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <InputLabel value="Warna Overlay" />
                  <div class="flex items-center gap-2 mt-1">
                    <input v-model="form.hero_color" type="color" class="w-10 h-10 border border-gray-300 rounded cursor-pointer" />
                    <input v-model="form.hero_color" type="text" placeholder="#7a2f2f"
                      class="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg" />
                  </div>
                </div>
                <div>
                  <InputLabel value="Gambar Hero" />
                  <input type="file" accept="image/*" @change="onFile"
                    class="block w-full mt-1 text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700" />
                </div>
              </div>
            </div>

            <!-- Konten -->
            <div>
              <InputLabel value="Konten Halaman" />
              <div class="mt-1 overflow-hidden border border-gray-200 rounded-xl">
                <QuillEditor v-model="form.content" />
              </div>
            </div>

            <div class="flex items-center gap-3">
              <PrimaryBtn :disabled="saving" @click="save"
                class="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {{ saving ? "Menyimpan..." : (form.id ? "Update Menu" : "Simpan Menu") }}
              </PrimaryBtn>
              <button v-if="form.id" type="button" @click="resetForm"
                class="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Batal</button>
            </div>
          </div>
        </div>

        <!-- Daftar -->
        <div class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-xs font-semibold tracking-wide text-left text-gray-500 uppercase">Menu</th>
                <th class="px-4 py-3 text-xs font-semibold tracking-wide text-left text-gray-500 uppercase">Slug</th>
                <th class="px-4 py-3 text-xs font-semibold tracking-wide text-center text-gray-500 uppercase">Status</th>
                <th class="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="m in menus" :key="m.id" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-sm font-medium text-gray-900">
                  <i v-if="m.icon" :class="m.icon" class="mr-2 text-gray-400"></i>{{ m.label }}
                </td>
                <td class="px-4 py-3 text-sm text-gray-500">
                  <a :href="`/halaman/${m.slug}`" target="_blank" class="text-indigo-600 hover:underline">/halaman/{{ m.slug }}</a>
                </td>
                <td class="px-4 py-3 text-center">
                  <span :class="m.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'"
                    class="inline-flex px-2 py-0.5 text-xs font-medium rounded-full">
                    {{ m.is_active ? "Aktif" : "Nonaktif" }}
                  </span>
                </td>
                <td class="px-4 py-3 text-right whitespace-nowrap">
                  <button @click="editMenu(m)" class="px-2.5 py-1 mr-1 text-xs font-medium text-indigo-600 rounded bg-indigo-50 hover:bg-indigo-100">Edit</button>
                  <button @click="remove(m)" class="px-2.5 py-1 text-xs font-medium text-red-600 rounded bg-red-50 hover:bg-red-100">Hapus</button>
                </td>
              </tr>
              <tr v-if="!menus.length">
                <td colspan="4" class="px-4 py-6 text-sm text-center text-gray-400">Belum ada menu dinamis.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { reactive, ref } from "vue";
import { router } from "@inertiajs/vue3";
import Swal from "sweetalert2";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputLabel from "@/Components/InputLabel.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import QuillEditor from "@/Components/QuillEditor.vue";

const props = defineProps({
  menus: { type: Array, default: () => [] },
});

const saving = ref(false);
const errors = reactive({});
const emptyForm = () => ({
  id: null, label: "", slug: "", icon: "", order: 0, is_active: true,
  hero_title: "", hero_subtitle: "", hero_color: "#7a2f2f", hero_image: null, content: "",
});
const form = reactive(emptyForm());

const onFile = (e) => { form.hero_image = e.target.files?.[0] || null; };

const resetForm = () => { Object.assign(form, emptyForm()); Object.keys(errors).forEach((k) => delete errors[k]); };

const editMenu = (m) => {
  Object.assign(form, {
    id: m.id, label: m.label, slug: m.slug, icon: m.icon || "", order: m.order, is_active: m.is_active,
    hero_title: m.hero_title || "", hero_subtitle: m.hero_subtitle || "",
    hero_color: m.hero_color || "#7a2f2f", hero_image: null, content: m.content || "",
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const save = () => {
  saving.value = true;
  Object.keys(errors).forEach((k) => delete errors[k]);
  const fd = new FormData();
  fd.append("label", form.label || "");
  fd.append("slug", form.slug || "");
  fd.append("icon", form.icon || "");
  fd.append("order", form.order ?? 0);
  fd.append("is_active", form.is_active ? "1" : "0");
  fd.append("hero_title", form.hero_title || "");
  fd.append("hero_subtitle", form.hero_subtitle || "");
  fd.append("hero_color", form.hero_color || "");
  fd.append("content", form.content || "");
  if (form.hero_image instanceof File) fd.append("hero_image", form.hero_image);

  const url = form.id ? route("menupages.update", form.id) : route("menupages.store");
  router.post(url, fd, {
    preserveScroll: true,
    forceFormData: true,
    onSuccess: () => {
      resetForm();
      Swal.fire({ title: "Berhasil!", icon: "success", timer: 1400, showConfirmButton: false });
    },
    onError: (e) => { Object.assign(errors, e); Swal.fire({ title: "Gagal", text: "Periksa input.", icon: "error" }); },
    onFinish: () => { saving.value = false; },
  });
};

const remove = (m) => {
  Swal.fire({ title: "Hapus menu?", text: m.label, icon: "warning", showCancelButton: true, confirmButtonText: "Hapus", confirmButtonColor: "#dc2626" })
    .then((r) => {
      if (r.isConfirmed) {
        router.delete(route("menupages.destroy", m.id), {
          preserveScroll: true,
          onSuccess: () => Swal.fire({ title: "Terhapus", icon: "success", timer: 1200, showConfirmButton: false }),
        });
      }
    });
};
</script>
